import { useState, useEffect } from "react";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { useLocalization } from "@/hooks/useLocalization";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Gift, Share2, Eye, EyeOff, QrCode, Sparkles, Clock } from "lucide-react";
import QRCode from "react-qr-code";

interface LoyaltyAccount {
  customer_id: string;
  points: number;
  target: number;
  voucher_code: string;
  accepted_terms: boolean;
}

interface Referral {
  id: string;
  referee_email: string;
  status: 'pending' | 'redeemed';
  created_at: string;
  redeemed_at?: string;
}

export default function CustomerLoyalty() {
  const { customer } = useCustomerAuth();
  const { t } = useLocalization();
  const [loyaltyData, setLoyaltyData] = useState<LoyaltyAccount | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [codeRevealed, setCodeRevealed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [revealTimeout, setRevealTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (customer) {
      fetchLoyaltyData();
    }
  }, [customer]);

  const fetchLoyaltyData = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke('customer-loyalty', {
        body: {
          customer_email: customer?.email,
          customer_id: customer?.id
        }
      });

      if (error) {
        console.error('Error fetching loyalty data:', error);
        return;
      }

      if (data?.success) {
        setLoyaltyData(data.loyalty);
        setReferrals(data.referrals || []);
        setTermsAccepted(data.loyalty?.accepted_terms || false);
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const revealCode = () => {
    setCodeRevealed(true);
    if (revealTimeout) clearTimeout(revealTimeout);
    
    const timeout = setTimeout(() => {
      setCodeRevealed(false);
    }, 15000);
    
    setRevealTimeout(timeout);
  };

  const shareReferral = async () => {
    if (!loyaltyData) return;

    const shareData = {
      title: t('get_discount_appointment'),
      text: t('use_referral_code').replace('{code}', loyaltyData.voucher_code),
      url: window.location.origin
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log(t('share_cancelled'));
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(t('copy_referral_fallback').replace('{code}', loyaltyData.voucher_code).replace('{url}', window.location.origin));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">{t('loading_rewards')}</p>
        </div>
      </div>
    );
  }

  const progressPercentage = loyaltyData ? (loyaltyData.points / loyaltyData.target) * 100 : 0;

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <div className="space-y-6">
        {/* Coming Soon Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-6 w-6 text-primary animate-pulse" />
            <h1 className="text-2xl font-semibold text-foreground">{t('loyalty_referrals')}</h1>
            <Sparkles className="h-6 w-6 text-primary animate-pulse" />
          </div>
          <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-800">
            <Clock className="h-3 w-3 mr-1" />
            {t('coming_soon_badge')}
          </Badge>
          <p className="text-sm text-muted-foreground">
            {t('earn_rewards_referring')}
          </p>
        </div>

        {/* Progress Card */}
        <Card className="overflow-hidden border-2 border-dashed border-primary/30 bg-gradient-to-br from-background via-background to-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                {t('loyalty_progress')}
              </CardTitle>
              <Badge variant="outline" className="opacity-60">
                {t('preview')}
              </Badge>
            </div>
            <CardDescription>
              {t('refer_friends').replace('{target}', String(loyaltyData?.target || 3))}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('progress')}</span>
                <span className="font-medium">
                  {loyaltyData?.points || 0} / {loyaltyData?.target || 3} {t('referrals')}
                </span>
              </div>
              <Progress 
                value={progressPercentage} 
                className="h-3 bg-muted"
                style={{
                  background: 'linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 100%)'
                }}
              />
            </div>
            
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <Gift className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{t('reward_veil_cleaning')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Voucher Card */}
        <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-background via-background to-secondary/5 opacity-75">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              {t('your_referral_code')}
            </CardTitle>
            <CardDescription>
              {t('share_code_friends')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Code Section */}
            <div className="text-center space-y-4">
              <div 
                className="relative p-6 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg border-2 border-dashed border-primary/30 cursor-not-allowed"
              >
                {codeRevealed ? (
                  <div className="space-y-2">
                    <p className="text-2xl font-bold tracking-widest text-primary">
                      {loyaltyData?.voucher_code || 'BRIDE2024'}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('code_hide_15s')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2">
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      <p className="text-muted-foreground">{t('tap_reveal_code')}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('preview_mode')}</p>
                  </div>
                )}
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg border-2 border-dashed border-primary/30 opacity-60">
                  <QRCode
                    value={loyaltyData?.voucher_code || 'BRIDE2024'}
                    size={120}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-center">
                <Button 
                  variant="outline" 
                  disabled
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {t('reveal_code')}
                </Button>
                <Button 
                  disabled
                  className="flex items-center gap-2"
                >
                  <Share2 className="h-4 w-4" />
                  {t('share')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card className="border-2 border-dashed border-primary/30 opacity-75">
          <CardContent className="p-6">
            <Accordion type="single" collapsible>
              <AccordionItem value="terms" className="border-none">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-medium">{t('terms_conditions')}</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 text-sm text-muted-foreground">
                  <div className="max-h-32 overflow-y-auto space-y-2 pr-2">
                    <p>1. {t('referral_valid_new_customers')}</p>
                    <p>2. {t('friends_book_deposit')}</p>
                    <p>3. {t('email_one_use_only')}</p>
                    <p>4. {t('rewards_after_booking')}</p>
                    <p>5. {t('points_expire_inactivity')}</p>
                    <p>6. {t('rewards_no_cash_exchange')}</p>
                    <p>7. {t('terms_subject_change')}</p>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="terms" 
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                      disabled
                    />
                    <label 
                      htmlFor="terms" 
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('accept_terms')}
                    </label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        <Card className="border-2 border-dashed border-primary/30 opacity-75">
          <CardHeader>
            <CardTitle className="text-lg">{t('recent_referrals')}</CardTitle>
            <CardDescription>{t('track_referrals')}</CardDescription>
          </CardHeader>
          <CardContent>
            {referrals.length > 0 ? (
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-dashed">
                    <div>
                      <p className="text-sm font-medium">{referral.referee_email}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge 
                      variant={referral.status === 'redeemed' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {t(referral.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('no_referrals_yet')}</p>
                <p className="text-xs">{t('start_sharing')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Notice */}
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <Sparkles className="h-4 w-4" />
              <p className="text-sm font-medium">
                {t('loyalty_preview_notice')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}